/**
 * Menu / navigation reference data — the single source of truth mapping every
 * page in the app's navigation to its page id, slug, and display name.
 *
 * Generated by create-frontend-layout-components from the `menuData` variable
 * declared in RawHTMLDesign/layouts/*.html (the design reference's navigation
 * data) plus the page catalog (RawHTMLDesign/PageDesignMap.json + each page's
 * synced knowledge). Every layout's nav renders from `menuTree` below; routing
 * is always by **slug**, never by page id.
 *
 * `route` starts pointed at the generic 404 page for every entry — none of
 * these pages exist yet. `ui-components-from-reference` re-points a single
 * entry's `route` to `/<slug>` the moment it builds that page's real
 * component (matched by `slug`), per its own workflow step 7. Do not
 * hand-edit `route` elsewhere; update this file's `pageRegistry` instead so
 * the nav and any other consumer stay in sync.
 */

const FALLBACK_ROUTE = '/404';

/**
 * Flat registry — one entry per page reachable from the navigation.
 * `pageId` is the synced page's scope id (RawHTMLDesign/PageDesignMap.json).
 */
export const pageRegistry = [
  { pageId: '2977b328-7aea-4ca1-8541-7dfb3116684c', slug: 'proposal-studio', name: 'Proposal Studio Workspace', route: '/proposal-studio' },
  { pageId: 'f4494f85-8a62-43b9-9ae0-5af2f38ffe01', slug: 'proposal-approvals-queue', name: 'Approvals Queue', route: '/proposal-approvals-queue' },
  { pageId: '4145d546-421d-4f32-b00b-b5533009e167', slug: 'proposal-template-library', name: 'Proposal Template Library', route: '/proposal-template-library' },
  { pageId: '00155154-81df-4622-a59e-8a7080257c6e', slug: 'proposal-history-versioning', name: 'Proposal Revision History', route: '/proposal-history-versioning' },
  { pageId: 'ea4a1a4c-f81e-4c78-9def-c00624c56d0a', slug: 'contract-term-repository', name: 'Contract Term Repository', route: '/contract-term-repository' },
  { pageId: 'd420b671-53f3-4fad-8487-99df7a3d62db', slug: 'contract-template-library', name: 'Contract Template Library', route: '/contract-template-library' },
  { pageId: 'd115e365-26e8-418c-a851-f73d4e15ac18', slug: 'document-library-signatures', name: 'Document & E-Sign Hub', route: '/document-esign-hub' },
  { pageId: '0aca329f-d181-45ba-a722-8bb197cf10f2', slug: 'milestone-signoff-billing-workspace', name: 'Milestone Billing Workspace', route: '/milestone-signoff-billing-workspace' },
  { pageId: '00c11360-433f-4535-a0b4-84c35ea44cb9', slug: 'oaas-metering-usage-portal', name: 'OaaS Usage Metering', route: '/oaas-metering-usage-portal' },
  { pageId: 'd3c25058-c91a-4c0a-a935-1c2861007317', slug: 'collections-dunning-command-center', name: 'Collections & Dunning Command', route: '/collections-dunning-command-center' },
  { pageId: '6a09791c-a0d1-4096-b2ff-890aa1e4a35a', slug: 'payer-profile-scorecard', name: 'Payer Intelligence Scorecard', route: '/payer-profile-scorecard' },
  { pageId: 'dfb10a23-6bc0-4e6e-ad68-03254dce7d58', slug: 'engagement-lifecycle-tracker-cashflow-hub', name: 'Engagement & Cashflow Hub', route: '/engagement-lifecycle-tracker-cashflow-hub' },
  { pageId: '3c46099b-969e-450c-80e1-4de6db8db276', slug: 'integration-connectors-hub', name: 'Integration Connectors Hub', route: '/integration-connectors-hub' },
  { pageId: 'dbb0b653-1c48-488c-b2a7-3f9f9dd0295a', slug: 'tenant-settings-rate-cards', name: 'Tenant Settings & Rate Cards', route: '/tenant-settings-rate-cards' },
  { pageId: '7bdd6780-6468-4413-aa56-a21c90245355', slug: 'user-access-rbac-console', name: 'User Access & RBAC Console', route: '/user-access-rbac-console' },
  { pageId: '1a138e57-b82a-4468-88b8-511311de7816', slug: 'active-project-delivery-hub', name: 'Active Project Delivery Hub', route: '/active-project-delivery-hub' },
  { pageId: 'cef9c2fc-677e-4fda-adaa-0db3757da804', slug: 'milestone-billing-proof-workspace', name: 'Milestone Billing Proof', route: '/milestone-billing-proof-workspace' },
  { pageId: 'f25aff58-6c0e-491f-8bea-422d52562457', slug: 't-m-evidence-expense-ledger', name: 'T&M Evidence & Expense Ledger', route: '/t-m-evidence-expense-ledger' },
  { pageId: '9f8a3f0b-3ed6-4597-97e9-9ecb0b115ba5', slug: 'account-manager-reminder-queue', name: 'Account Manager Reminder Queue', route: '/account-manager-reminder-queue' },

  // Platform pages, not from the DesignStudio catalog — they carry no scope
  // pageId because no design page defines them. They are operational surfaces
  // for capabilities that already existed in the backend with no interface.
  { pageId: null, slug: 'renewal-radar', name: 'Renewal Radar', route: '/renewal-radar' },
  { pageId: null, slug: 'revenue-attribution', name: 'Revenue Attribution', route: '/revenue-attribution' },
  { pageId: null, slug: 'einvoicing-compliance', name: 'UAE E-Invoicing Compliance', route: '/einvoicing-compliance' },
  { pageId: null, slug: 'scheduler-console', name: 'Scheduler Console', route: '/scheduler-console' },

  // Not part of the top navigation (auth-web-layout serves it standalone),
  // kept here so it has one canonical id/slug/route entry too.
  { pageId: 'c70914b5-26ea-4175-9fa3-8196290e98e4', slug: 'login', name: 'Login Page', route: '/login' },
];

export function findPageBySlug(slug) {
  return pageRegistry.find((p) => p.slug === slug) || null;
}

export function routeForSlug(slug) {
  return findPageBySlug(slug)?.route || FALLBACK_ROUTE;
}

/**
 * Grouped navigation tree, mirroring the `menuData.Children` structure
 * declared in the raw layout designs (label + icon + grouping). Each leaf
 * references a `pageRegistry` slug; its route is resolved live via
 * `routeForSlug` so re-pointing `pageRegistry` is the only edit ever needed.
 */
export const menuTree = [
  {
    label: 'Proposal Studio Workspace',
    icon: 'fa fa-file-text',
    children: [
      { label: 'Proposal Studio Workspace', icon: 'fa fa-edit', slug: 'proposal-studio' },
      { label: 'Approvals Queue', icon: 'fa fa-check-square', slug: 'proposal-approvals-queue' },
      { label: 'Proposal Template Library', icon: 'fa fa-layer-group', slug: 'proposal-template-library' },
      { label: 'Proposal Revision History', icon: 'fa fa-history', slug: 'proposal-history-versioning' },
    ],
  },
  {
    label: 'Contract & Documents',
    icon: 'fa fa-file-contract',
    children: [
      { label: 'Contract Term Repository', icon: 'fa fa-file-contract', slug: 'contract-term-repository' },
      { label: 'Contract Template Library', icon: 'fa fa-file-signature', slug: 'contract-template-library' },
      { label: 'Document & E-Sign Hub', icon: 'fa fa-folder-open', slug: 'document-library-signatures' },
      { label: 'Renewal Radar', icon: 'fa fa-redo', slug: 'renewal-radar' },
    ],
  },
  {
    label: 'Billing & Operations',
    icon: 'fa fa-calculator',
    children: [
      { label: 'Milestone Billing Workspace', icon: 'fa fa-file-invoice-dollar', slug: 'milestone-signoff-billing-workspace' },
      { label: 'OaaS Usage Metering', icon: 'fa fa-tachometer-alt', slug: 'oaas-metering-usage-portal' },
      { label: 'UAE E-Invoicing Compliance', icon: 'fa fa-file-invoice', slug: 'einvoicing-compliance' },
    ],
  },
  {
    label: 'Lifecycle & Cashflow',
    icon: 'fa fa-chart-line',
    children: [
      { label: 'Collections & Dunning Command', icon: 'fa fa-hand-holding-usd', slug: 'collections-dunning-command-center' },
      { label: 'Payer Intelligence Scorecard', icon: 'fa fa-user-shield', slug: 'payer-profile-scorecard' },
      { label: 'Engagement & Cashflow Hub', icon: 'fa fa-chart-bar', slug: 'engagement-lifecycle-tracker-cashflow-hub' },
      { label: 'Revenue Attribution', icon: 'fa fa-bullseye', slug: 'revenue-attribution' },
    ],
  },
  {
    label: 'System Administration',
    icon: 'fa fa-cogs',
    children: [
      { label: 'Integration Connectors Hub', icon: 'fa fa-plug', slug: 'integration-connectors-hub' },
      { label: 'Tenant Settings & Rate Cards', icon: 'fa fa-sliders-h', slug: 'tenant-settings-rate-cards' },
      { label: 'User Access & RBAC Console', icon: 'fa fa-user-cog', slug: 'user-access-rbac-console' },
      { label: 'Scheduler Console', icon: 'fa fa-clock', slug: 'scheduler-console' },
    ],
  },
  {
    label: 'Project Delivery & Ops',
    icon: 'fa fa-tasks',
    children: [
      { label: 'Active Project Delivery Hub', icon: 'fa fa-project-diagram', slug: 'active-project-delivery-hub' },
      { label: 'Milestone Billing Proof', icon: 'fa fa-certificate', slug: 'milestone-billing-proof-workspace' },
      { label: 'T&M Evidence & Expense Ledger', icon: 'fa fa-receipt', slug: 't-m-evidence-expense-ledger' },
      { label: 'Account Manager Reminder Queue', icon: 'fa fa-bell', slug: 'account-manager-reminder-queue' },
    ],
  },
];

export const defaultMenuItemSlug = 'proposal-studio';
